import {
    useForm,
    Controller,
    FormProvider,
    SubmitHandler
} from "react-hook-form";
import { getProductDisplayName, Product } from "types/product";
import "../../../../assets/styles/leadProviders/importLead/importLeadCreateManually.scss";
import {
    Autocomplete,
    Box,
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    TextField,
    Tooltip
} from "@mui/material";
import { useMemo, useState } from "react";
import { DictionaryEditor } from "./DictionaryEditor";
import { ConfirmModal } from "./ConfirmModal";
import { createImportLeadFromValidation } from "../hooks/createImportLeadFromValidation";
import { CareLevelEnum, getCareLevelDisplayName } from "types/careLevel";
import { generateUUID } from "utils";
import { usePostCreateImportLead } from "../service/ImportLeadService";
import { convertManuallyLeadFormDtoRequest } from "../utils/ImportLeadCreateManually/mapper";
import { useNavigate } from "react-router-dom";
import { CustomTextArea } from "components/ui/inputs";

interface CustomProperty {
    key: string;
    value: string;
}

export interface ManuallyLeadFormValues {
    leadId: string;
    clientTitle: string;
    clientFirstName: string;
    clientLastName: string;
    clientEmail: string;
    clientPhoneNumber: string;
    requestZipCode: string;
    requestRegion: string;
    source: string;
    productId: Product | undefined;
    comments: string;
    clientAge: string;
    clientCareLevel: CareLevelEnum | undefined;
    customPropertiesList: CustomProperty[];
}

export default function ImportLeadCreateManually() {
    const navigate = useNavigate();
    const methods = useForm<ManuallyLeadFormValues>({
        defaultValues: {
            leadId: generateUUID(),
            clientTitle: "",
            clientFirstName: "",
            clientLastName: "",
            clientEmail: "",
            clientPhoneNumber: "",
            requestZipCode: "",
            requestRegion: "",
            source: "",
            productId: undefined,
            clientCareLevel: undefined,
            clientAge: "",
            comments: "",
            customPropertiesList: []
        }
    });

    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingData, setPendingData] =
        useState<ManuallyLeadFormValues | null>(null);
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors, isDirty, dirtyFields }
    } = methods;

    const { emailAddressRules, mobileNumberRules, zipCodeRules, ageRules } =
        createImportLeadFromValidation();

    const { mutate: sendLead } = usePostCreateImportLead({
        onSuccess: () => {
            submitOnSuccess();
        }
    });

    const vorname = watch("clientFirstName") || "";
    const nachname = watch("clientLastName") || "";
    const email = watch("clientEmail") || "";
    const phoneNumber = watch("clientPhoneNumber") || "";
    const zipCode = watch("requestZipCode") || "";
    const region = watch("requestRegion") || "";
    const product = watch("productId") || undefined;
    const vornameEmpty = !vorname.trim();
    const nachnameEmpty = !nachname.trim();
    const emailEmpty = !email.trim();
    const phoneNumberEmpty = !phoneNumber.trim();
    const zipCodeEmpty = !zipCode.trim();
    const regionEmpty = !region.trim();
    const productEmpty = !product;

    const productOptions = useMemo(() => {
        return Object.values(Product).filter(
            (value) => typeof value === "number"
        ) as Product[];
    }, []);

    const careLevelEnumOptions = useMemo(() => {
        return Object.values(CareLevelEnum).filter(
            (value) => typeof value === "number"
        ) as CareLevelEnum[];
    }, []);

    const onSubmitAttempt: SubmitHandler<ManuallyLeadFormValues> = (data) => {
        setAttemptedSubmit(true);
        if (!data.clientFirstName.trim() || !data.clientLastName.trim()) {
            // Show modal if either field is empty
            setPendingData(data);
            setShowConfirm(true);
        } else {
            handleRealSubmit(data);
        }
    };

    const handleRealSubmit = (data: ManuallyLeadFormValues) => {
        const convertedData = convertManuallyLeadFormDtoRequest(data);
        sendLead(convertManuallyLeadFormDtoRequest(data));
    };

    const handleConfirmSave = () => {
        if (pendingData) {
            handleRealSubmit(pendingData);
        }
        setShowConfirm(false);
        setPendingData(null);
    };

    const handleCancel = () => {
        setShowConfirm(false);
        setPendingData(null);
    };

    function submitOnSuccess() {
        navigate("/LeadOverview");
    }

    return (
        <div className="container">
            <h1 className="text-blue mtb-25">Klient erstellen</h1>
            <div className="flex-wrap align-center">
                <h3 className="highlight-green mtb-0">
                    Haben Sie Ihren eigenen Klienten?
                </h3>
            </div>
            <p className="text-black">
                Fügen Sie Ihre eigenen Klienten ganz einfach dem
                Anfragen-Manager hinzu und verwalten Sie{" "}
                <strong>alle digital an einem zentralen Ort.</strong>
            </p>
            <div className="create-request-container">
                <p className="change-request-form-heading">Neuer Klient</p>
                <FormProvider {...methods}>
                    <div className="create-request-form-container">
                        <form onSubmit={handleSubmit(onSubmitAttempt)}>
                            <div className="">
                                <div className="col-6">
                                    <label className="bolder" htmlFor="anrede">
                                        Anrede:
                                    </label>
                                    <Box className="input-dropdown">
                                        <Controller
                                            name="clientTitle"
                                            control={control}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <FormControl
                                                    fullWidth
                                                    variant="outlined"
                                                    margin="normal"
                                                    error={!!error}
                                                >
                                                    <Select
                                                        labelId="anrede-label"
                                                        label="Anrede"
                                                        {...field}
                                                        displayEmpty
                                                    >
                                                        <MenuItem
                                                            value=""
                                                            className="non-bold-text"
                                                        >
                                                            Anrede auswählen
                                                        </MenuItem>
                                                        <MenuItem
                                                            value="Frau"
                                                            className="non-bold-text"
                                                        >
                                                            Frau
                                                        </MenuItem>
                                                        <MenuItem
                                                            value="Herr"
                                                            className="non-bold-text"
                                                        >
                                                            Herr
                                                        </MenuItem>
                                                    </Select>
                                                    {error && (
                                                        <FormHelperText>
                                                            {error.message}
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Box>
                                </div>
                            </div>

                            <div className="flex">
                                <div className="col-6 mr-15-mobile">
                                    <label className="bolder">Vorname:</label>
                                    <Box className="input">
                                        <Controller
                                            name="clientFirstName"
                                            control={control}
                                            defaultValue=""
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        setAttemptedSubmit(
                                                            false
                                                        );
                                                    }}
                                                    className={`empty-bg-change ${
                                                        attemptedSubmit &&
                                                        vornameEmpty
                                                            ? "highlight-empty"
                                                            : ""
                                                    }`}
                                                    placeholder="Vorname"
                                                    variant="outlined"
                                                    fullWidth
                                                    margin="normal"
                                                    inputProps={{
                                                        maxLength: 50
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </div>
                                <div className="col-6">
                                    <label className="bolder">Nachname:</label>
                                    <Box className="input">
                                        <Controller
                                            name="clientLastName"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        setAttemptedSubmit(
                                                            false
                                                        );
                                                    }}
                                                    className={`empty-bg-change ${
                                                        attemptedSubmit &&
                                                        nachnameEmpty
                                                            ? "highlight-empty"
                                                            : ""
                                                    }`}
                                                    placeholder="Nachname"
                                                    variant="outlined"
                                                    fullWidth
                                                    margin="normal"
                                                    inputProps={{
                                                        maxLength: 50
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </div>
                            </div>

                            <div className="flex">
                                <div className="col-6 mr-15-mobile">
                                    <label className="bolder">
                                        E-Mail-Adresse:
                                    </label>
                                    <Box className="input">
                                        <Controller
                                            name="clientEmail"
                                            control={control}
                                            defaultValue=""
                                            rules={emailAddressRules}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <TextField
                                                    {...field}
                                                    placeholder="E-Mail-Adresse"
                                                    variant="outlined"
                                                    fullWidth
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        setAttemptedSubmit(
                                                            false
                                                        );
                                                    }}
                                                    error={!!error}
                                                    helperText={
                                                        error
                                                            ? error.message
                                                            : ""
                                                    }
                                                    className={`empty-bg-change ${
                                                        attemptedSubmit &&
                                                        emailEmpty
                                                            ? "highlight-empty"
                                                            : ""
                                                    }`}
                                                    margin="normal"
                                                    inputProps={{
                                                        maxLength: 50
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </div>
                                <div className="col-6">
                                    <label className="bolder">
                                        Telefonnummer:
                                    </label>
                                    <Box className="input">
                                        <Controller
                                            name="clientPhoneNumber"
                                            control={control}
                                            defaultValue=""
                                            rules={mobileNumberRules}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <TextField
                                                    {...field}
                                                    placeholder="Telefonnummer"
                                                    variant="outlined"
                                                    fullWidth
                                                    error={!!error}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        setAttemptedSubmit(
                                                            false
                                                        );
                                                    }}
                                                    helperText={
                                                        error
                                                            ? error.message
                                                            : null
                                                    }
                                                    className={`empty-bg-change ${
                                                        attemptedSubmit &&
                                                        phoneNumberEmpty
                                                            ? "highlight-empty"
                                                            : ""
                                                    }`}
                                                    margin="normal"
                                                />
                                            )}
                                        />
                                    </Box>
                                </div>
                            </div>

                            <div className="flex">
                                <div className="col-6 mr-15-mobile">
                                    <label className="bolder">
                                        Postleitzahl:
                                    </label>
                                    <Box className="input">
                                        <Controller
                                            name="requestZipCode"
                                            control={control}
                                            defaultValue=""
                                            rules={zipCodeRules}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <TextField
                                                    {...field}
                                                    placeholder="Postleitzahl"
                                                    variant="outlined"
                                                    error={!!error}
                                                    helperText={
                                                        error
                                                            ? error.message
                                                            : null
                                                    }
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        setAttemptedSubmit(
                                                            false
                                                        );
                                                    }}
                                                    className={`empty-bg-change ${
                                                        attemptedSubmit &&
                                                        zipCodeEmpty
                                                            ? "highlight-empty"
                                                            : ""
                                                    }`}
                                                    fullWidth
                                                    margin="normal"
                                                    inputProps={{
                                                        maxLength: 50
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </div>

                                <div className="col-6">
                                    <label className="bolder">Ort:</label>
                                    <Box className="input">
                                        <Controller
                                            name="requestRegion"
                                            control={control}
                                            defaultValue=""
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <TextField
                                                    {...field}
                                                    placeholder="Ort"
                                                    variant="outlined"
                                                    error={!!error}
                                                    helperText={
                                                        error
                                                            ? error.message
                                                            : null
                                                    }
                                                    fullWidth
                                                    margin="normal"
                                                    inputProps={{
                                                        maxLength: 50
                                                    }}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        setAttemptedSubmit(
                                                            false
                                                        );
                                                    }}
                                                    className={`empty-bg-change ${
                                                        attemptedSubmit &&
                                                        regionEmpty
                                                            ? "highlight-empty"
                                                            : ""
                                                    }`}
                                                />
                                            )}
                                        />
                                    </Box>
                                </div>
                            </div>

                            <div className="flex">
                                <div className="col-6 mr-15-mobile">
                                    <label className="bolder" htmlFor="bedarf">
                                        Bedarf:
                                    </label>
                                    <Box className="input-dropdown product-dropdown">
                                        <Controller
                                            name="productId"
                                            control={control}
                                            defaultValue={undefined} // No selection
                                            render={({
                                                field: { onChange, value }
                                            }) => (
                                                <Autocomplete
                                                    options={productOptions}
                                                    getOptionLabel={(option) =>
                                                        getProductDisplayName(
                                                            option
                                                        )
                                                    }
                                                    className={` ${
                                                        attemptedSubmit &&
                                                        productEmpty
                                                            ? "highlight-empty"
                                                            : ""
                                                    }`}
                                                    value={
                                                        value != undefined &&
                                                        value != null &&
                                                        value >= 0
                                                            ? value
                                                            : null
                                                    }
                                                    onChange={(
                                                        event,
                                                        newValue
                                                    ) => {
                                                        setAttemptedSubmit(
                                                            false
                                                        );
                                                        onChange(newValue);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Bedarf auswählen"
                                                            variant="outlined"
                                                            margin="normal"
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                    </Box>
                                </div>
                                <div className="col-6">
                                    <label className="bolder">Alter:</label>
                                    <Box className="input">
                                        <Controller
                                            name="clientAge"
                                            control={control}
                                            defaultValue=""
                                            rules={ageRules}
                                            render={({
                                                field,
                                                fieldState: { error }
                                            }) => (
                                                <TextField
                                                    {...field}
                                                    type="number"
                                                    placeholder="Alter"
                                                    className="empty-bg-change"
                                                    variant="outlined"
                                                    error={!!error}
                                                    helperText={
                                                        error
                                                            ? error.message
                                                            : null
                                                    }
                                                    fullWidth
                                                    margin="normal"
                                                    inputProps={{
                                                        maxLength: 50
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </div>
                            </div>

                            <div className="flex">
                                <div className="col-6 mr-15-mobile">
                                    <label
                                        className="bolder"
                                        htmlFor="pflegegrad
"
                                    >
                                        Pflegegrad:
                                    </label>
                                    <Box className="input-dropdown">
                                        <Controller
                                            name="clientCareLevel"
                                            control={control}
                                            defaultValue={undefined} // "no selection"
                                            render={({ field }) => (
                                                <FormControl
                                                    fullWidth
                                                    variant="outlined"
                                                    margin="normal"
                                                >
                                                    <Select
                                                        labelId="pflegegrad-label"
                                                        label="Pflegegrad"
                                                        value={
                                                            field.value ?? ""
                                                        }
                                                        onChange={(e) => {
                                                            // 2) Convert "" back to undefined, else keep the selected product
                                                            const newValue =
                                                                e.target
                                                                    .value ===
                                                                ""
                                                                    ? undefined
                                                                    : e.target
                                                                          .value;
                                                            field.onChange(
                                                                newValue
                                                            );
                                                        }}
                                                        displayEmpty
                                                        renderValue={(
                                                            selected:
                                                                | CareLevelEnum
                                                                | ""
                                                        ) => {
                                                            // 3) If the current form value is undefined, show the placeholder text
                                                            if (
                                                                selected === ""
                                                            ) {
                                                                return (
                                                                    <span>
                                                                        Pflegegrad
                                                                        auswählen
                                                                    </span>
                                                                );
                                                            }
                                                            return (
                                                                <span>
                                                                    {getCareLevelDisplayName(
                                                                        selected
                                                                    )}
                                                                </span>
                                                            );
                                                        }}
                                                    >
                                                        <MenuItem
                                                            value=""
                                                            className="hidden"
                                                        ></MenuItem>

                                                        {careLevelEnumOptions.map(
                                                            (pflegegrad) => (
                                                                <MenuItem
                                                                    key={
                                                                        pflegegrad
                                                                    }
                                                                    value={
                                                                        pflegegrad
                                                                    }
                                                                    className="non-bold-text"
                                                                >
                                                                    {getCareLevelDisplayName(
                                                                        pflegegrad
                                                                    )}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        />
                                    </Box>
                                </div>

                                <div className="col-6">
                                    <label className="bolder">Quelle:</label>
                                    <Box className="input-dropdown">
                                        <Controller
                                            name="source"
                                            control={control}
                                            defaultValue=""
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    className="empty-bg-change"
                                                    placeholder="Quelle"
                                                    variant="outlined"
                                                    fullWidth
                                                    margin="normal"
                                                    inputProps={{
                                                        maxLength: 50
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </div>
                            </div>

                            <DictionaryEditor name="customPropertiesList" />

                            <div className="input-textbox">
                                <div className="">
                                    <label className="bolder">
                                        Interner Kommentar:
                                    </label>
                                    <Box className="">
                                        <Controller
                                            name="comments"
                                            control={control}
                                            defaultValue=""
                                            render={({ field }) => (
                                                <>
                                                    <CustomTextArea
                                                        {...field}
                                                        placeholder="Interner Kommentar"
                                                    />
                                                </>
                                            )}
                                        />
                                    </Box>
                                </div>
                            </div>

                            <div className="create-request-button-container">
                                <Tooltip
                                    title={
                                        !isDirty
                                            ? "Bitte geben Sie mindestens in einem Feld Daten ein!"
                                            : ""
                                    }
                                    arrow
                                    // Disable the tooltip when the button is enabled
                                    disableHoverListener={isDirty}
                                    disableFocusListener={isDirty}
                                    disableTouchListener={isDirty}
                                    componentsProps={{
                                        tooltip: {
                                            className:
                                                "create-request-field-empty-tooltip"
                                        }
                                    }}
                                >
                                    {/* Wrapping the disabled button in a <span> so the tooltip can still appear */}
                                    <span className="full-width-button">
                                        <button
                                            type="submit"
                                            className="btn submit-button full-width-button"
                                            disabled={!isDirty}
                                        >
                                            Speichern
                                        </button>
                                    </span>
                                </Tooltip>
                            </div>
                        </form>
                    </div>
                </FormProvider>
                <ConfirmModal
                    open={showConfirm}
                    onCancel={handleCancel}
                    onConfirm={handleConfirmSave}
                    vornameEmpty={vornameEmpty}
                    nachnameEmpty={nachnameEmpty}
                    emailEmpty={emailEmpty}
                    phoneNumberEmpty={phoneNumberEmpty}
                    zipCodeEmpty={zipCodeEmpty}
                    regionEmpty={regionEmpty}
                    productEmpty={productEmpty}
                />
            </div>
        </div>
    );
}
